/**
 * Abnormal two-point discrimination that the rating silently ignores.
 *
 * Sensory impairment = max % × loss factor × digit-length %. With no digit-length
 * % recorded the product is 0, so a 12 mm two-point (an abnormal finding) adds
 * nothing and nothing on screen says why. That matches the QME - Tool reference
 * calculator, so the math stays; this lists the cases so the UI can say so.
 */
const DIGITS: [string, string][] = [
  ['thumbs', 'thumb'], ['index_fingers', 'index finger'], ['middle_fingers', 'middle finger'],
  ['ring_fingers', 'ring finger'], ['little_fingers', 'little finger'],
];
const ABNORMAL_2PD_MM = 6; // > 6 mm is a deficit (Table 16-5)

const present = (v: any) => v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v));

export function unratedSensory(examData: any): string[] {
  const out: string[] = [];
  for (const [key, label] of DIGITS) {
    const d = examData?.hands?.[key];
    if (!d) continue;
    for (const side of ['right', 'left']) {
      for (const nerve of ['radial', 'ulnar']) {
        const mm = d[`${side}_${nerve}_two_point`];
        const pct = d[`${side}_${nerve}_two_point_percent`];
        if (present(mm) && Number(mm) > ABNORMAL_2PD_MM && !(present(pct) && Number(pct) > 0)) {
          out.push(`${side[0].toUpperCase()}${side.slice(1)} ${label}, ${nerve} two-point ${Number(mm)} mm`);
        }
      }
    }
  }
  return out;
}

/**
 * Grip facts the report text asserts but the data may contradict: sides with 1
 * or 2 of 3 trials recorded, and trials that differ by more than 20%. The average uses the trials done
 * (correct), but the report text says the test was "repeated a total of three
 * times", so the doctor should know before pasting.
 */
export function incompleteGrip(examData: any): string[] {
  const g = examData?.grip_strength;
  if (!g || g.limited_by) return [];
  const out: string[] = [];
  for (const side of ['right', 'left']) {
    const vals = [1, 2, 3].map((i) => g[`${side}_attempt_${i}`]).filter(present).map(Number);
    const Side = `${side[0].toUpperCase()}${side.slice(1)}`;
    if (vals.length > 0 && vals.length < 3) out.push(`${Side} grip has ${vals.length} of 3 trials recorded`);
    // The report also asserts "no more than 20% difference" between trials.
    const hi = Math.max(...vals), lo = Math.min(...vals);
    if (vals.length > 1 && hi > 0 && (hi - lo) / hi > 0.2) {
      out.push(`${Side} grip trials differ by ${Math.round(((hi - lo) / hi) * 100)}% (${vals.join(', ')} kg), but the report says no more than 20%`);
    }
  }
  return out;
}

/**
 * The grip narrative states that none of the §16.8a exclusions apply, including
 * "a loss of motion". If the same report also rates motion loss in that arm,
 * the report contradicts itself (and grip should not be combined with it).
 * Takes the finished narratives; returns the rated regions that conflict.
 */
export function gripMotionConflict(narratives: string[]): string[] {
  const grip = narratives.find((n) => /^(Right|Left) Grip Strength:/.test(n) && !/No impairment based on grip/.test(n));
  if (!grip) return [];
  const side = grip.startsWith('Right') ? 'RIGHT' : 'LEFT';
  return narratives
    .filter((n) => new RegExp(`^<b><u>${side} (HAND|WRIST|ELBOW|SHOULDER):`).test(n) && !/No impairment/.test(n))
    .map((n) => n.match(/^<b><u>([A-Z ]+):/)![1].toLowerCase());
}
